import React from 'react';

/**
 * Field - Componente helper para formularios
 * Maneja label, hint, error y estados de accesibilidad
 */
export function Field({ label, hint, children, error, required = false }) {
  const id = React.useId();

  return (
    <div className="block text-sm text-[rgb(var(--fg))]">
      <label htmlFor={id} className="mb-2 block text-sm font-semibold text-[rgb(var(--fg))]">
        {label}
        {required && <span className="ml-1 text-[rgb(var(--brand))]" aria-label="Requerido">*</span>}
      </label>
      
      {/* Clonar el children para agregar el id */}
      {React.cloneElement(children, {
        id,
        'aria-describedby': hint || error ? `${id}-help` : undefined,
        'aria-invalid': error ? 'true' : undefined,
      })}
      
      {/* Hint o error */}
      {(hint || error) && (
        <div id={`${id}-help`} className="mt-1 block text-xs text-[rgb(var(--muted))]">
          {error ? (
            <span className="text-red-600 dark:text-red-400">{error}</span>
          ) : (
            <span>{hint}</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * FormCard - Contenedor de formulario con halo header y elevación
 */
export function FormCard({ children, className = '', withHalo = true }) {
  return (
    <section
      className={`relative rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.98)] shadow-[0_2px_8px_rgba(var(--shadow),0.06)] dark:bg-[rgba(var(--panel),0.92)] dark:shadow-[0_18px_48px_rgba(var(--shadow),0.35)] p-5 md:p-6 ${className}`}
    >
      {/* Halo header sutil */}
      {withHalo && (
        <div aria-hidden className="absolute -z-10 -top-6 left-8 h-24 w-24 rounded-full bg-[rgba(var(--brand),0.10)] blur-2xl" />
      )}
      {children}
    </section>
  );
}

/**
 * StickyActions - Footer pegado con acciones de formulario
 */
export function StickyActions({ children, saveHint = "⌘/Ctrl + S para guardar rápido" }) {
  return (
    <div className="sticky bottom-3 mt-6 -mx-5 md:-mx-6">
      <div className="flex items-center justify-end gap-3 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.95)] px-4 py-3 shadow-sm backdrop-blur">
        {saveHint ? (
          <span className="mr-auto text-xs text-[rgb(var(--muted))]">{saveHint}</span>
        ) : null}
        {children}
      </div>
    </div>
  );
}

/**
 * Input - Input base con estilos del theme
 */
export function Input({ className = '', ...props }) {
  return (
    <input
      className={`w-full min-h-[44px] rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 py-2 text-sm text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] focus:border-[rgb(var(--ring))] ${className}`}
      {...props}
    />
  );
}
export function Checkbox({ className = '', label, ...props }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer group">
      <input
        type="checkbox"
        className={`h-5 w-5 rounded border-2 border-[rgb(var(--border))] bg-[rgb(var(--panel))] text-[rgb(var(--brand))] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] focus:ring-offset-0 checked:bg-[rgb(var(--brand))] checked:border-[rgb(var(--brand))] ${className}`}
        {...props}
      />
      {label && (
        <span className="text-sm text-[rgb(var(--fg))] group-hover:text-[rgb(var(--brand))] transition-colors">
          {label}
        </span>
      )}
    </label>
  );
}

/**
 * Select - Select base con estilos del theme
 */
export function Select({ className = '', children, ...props }) {
  return (
    <select
      className={`w-full min-h-[44px] rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 py-2 text-sm text-[rgb(var(--fg))] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] focus:border-[rgb(var(--ring))] ${className}`}
      {...props}
    >
      {children}
    </select>
  );
}

/**
 * Textarea - Textarea base con estilos del theme
 */
export function Textarea({ className = '', ...props }) {
  return (
    <textarea
      className={`w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 py-2 text-sm text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] focus:border-[rgb(var(--ring))] resize-y min-h-[120px] ${className}`}
      {...props}
    />
  );
}

/**
 * Button - Botón base con variantes
 */
export function Button({ 
  variant = 'primary', 
  size = 'md', 
  className = '', 
  children, 
  ...props 
}) {
  const baseClasses = "inline-flex min-h-[44px] items-center justify-center rounded-xl font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] focus-visible:ring-offset-0";

  const variants = {
    primary: "bg-[rgb(var(--brand))] text-white shadow-sm hover:bg-[rgb(var(--brand-700))]",
    secondary: "border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.8)] text-[rgb(var(--fg))] shadow-[0_1px_2px_rgba(var(--shadow),0.08)] hover:bg-[rgba(var(--subpanel),0.6)]",
    ghost: "text-[rgb(var(--fg))] hover:bg-[rgba(var(--subpanel),0.6)]"
  };

  const sizes = {
    sm: "px-3 py-2 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base"
  };
  
  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}